import React, { useEffect, useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import { Modal, ModalBody } from "reactstrap";
import Select from "react-select";
import Text from "components/Text";
import Loader from "components/Loader";
import crossIcon from "assets/images/cross.svg";
import userDefaultImage from "assets/images/staff-default-rounded.png";
import { useAllStaffByDesignationIdAndOfficeId } from "repositories/notes-repository";
import {
  useAllActiveOffices,
  useStaffDesignation,
} from "repositories/scheduler-repository";
import styles from "staff/pages/StaffListingTimesheet/StaffListingTimesheet.module.scss";
import "staff/pages/StaffListingTimesheet/StaffListingTimesheet.scss";
import "accountOwner/pages/AddEvent/AddEvent.scss";
import toast from "react-hot-toast";
import localStyles from "./AddTimesheetStaffModal.module.scss";
import constants from "../../../../../constants";

/**
 * @param {boolean} [hideOfficeAndRoleFilters] When true, office/role dropdowns are hidden (report is already scoped to an office).
 */
function AddTimesheetStaffModal({
  t,
  isOpen,
  ownerId,
  onClose,
  onSelectStaff,
  lockOfficeFilter = false,
  hideOfficeAndRoleFilters = false,
  submitLabel = null,
  initialOfficeId = null,
  initialOfficeName = null,
}) {
  const selectStyles = useMemo(
    () => ({
      container: (provided) => ({
        ...provided,
        width: "100%",
      }),
      control: (provided) => ({
        ...provided,
        minHeight: "52px",
        borderRadius: "8px",
        borderColor: "#dadada",
        boxShadow: "none",
      }),
      indicatorSeparator: () => ({ display: "none" }),
      menu: (provided) => ({ ...provided, zIndex: 9999 }),
    }),
    [],
  );
  const [searchText, setSearchText] = useState("");
  const [selectedOfficeOption, setSelectedOfficeOption] = useState({
    value: "",
    label: t("notes.allOffices"),
  });
  const [selectedRoleOption, setSelectedRoleOption] = useState({
    value: "",
    label: t("officePolicyManagement.allRoles"),
  });
  const [selectedStaffId, setSelectedStaffId] = useState(null);

  const allOfficesOption = useMemo(
    () => ({ value: "", label: t("notes.allOffices") }),
    [t],
  );
  const allRolesOption = useMemo(
    () => ({ value: "", label: t("officePolicyManagement.allRoles") }),
    [t],
  );

  const { data: officeData, isLoading: officeLoading } = useAllActiveOffices(
    1,
    constants.ACTIVE_OFFICES_PAGE_SIZE,
    { ownerId, includeAdminOffice: false },
    "",
    {
      enabled: isOpen && !!ownerId && !hideOfficeAndRoleFilters,
    },
  );

  useEffect(() => {
    if (!isOpen) return;
    setSearchText("");
    setSelectedStaffId(null);
    setSelectedRoleOption(allRolesOption);
  }, [isOpen, allRolesOption]);

  useEffect(() => {
    if (!isOpen) return;
    if (initialOfficeId != null && initialOfficeId !== "") return;
    setSelectedOfficeOption(allOfficesOption);
  }, [isOpen, initialOfficeId, allOfficesOption]);

  useEffect(() => {
    if (!isOpen) return;
    if (initialOfficeId == null || initialOfficeId === "") return;

    const offices = officeData?.data || [];
    const match = offices.find(
      (office) => String(office?.id) === String(initialOfficeId),
    );
    if (match) {
      setSelectedOfficeOption({ value: match.id, label: match.name });
    } else {
      setSelectedOfficeOption({
        value: initialOfficeId,
        label: initialOfficeName || "",
      });
    }
  }, [isOpen, initialOfficeId, initialOfficeName, officeData]);

  const selectedOfficeIds = useMemo(() => {
    if (
      hideOfficeAndRoleFilters &&
      initialOfficeId != null &&
      initialOfficeId !== ""
    ) {
      return [initialOfficeId];
    }
    return selectedOfficeOption?.value ? [selectedOfficeOption.value] : [];
  }, [hideOfficeAndRoleFilters, initialOfficeId, selectedOfficeOption?.value]);
  const selectedDesignationIds = selectedRoleOption?.value
    ? [selectedRoleOption.value]
    : [];

  const { data: staffRoleData, isLoading: roleLoading } = useStaffDesignation(
    {},
    { enabled: isOpen && !!ownerId && !hideOfficeAndRoleFilters },
  );
  const { data: staffData, isLoading: staffLoading } =
    useAllStaffByDesignationIdAndOfficeId(
      selectedDesignationIds,
      selectedOfficeIds,
      { ownerId, IsOwnerInclude: false, ExcludeRequestor: true },
      { enabled: isOpen && !!ownerId },
    );

  const officeOptions = useMemo(() => {
    const dynamicOffices =
      officeData?.data?.map((office) => ({
        value: office?.id,
        label: office?.name,
      })) || [];

    return [{ value: "", label: t("notes.allOffices") }, ...dynamicOffices];
  }, [officeData, t]);

  const roleOptions = useMemo(() => {
    const designationOptions =
      staffRoleData
        ?.map((item) => item?.designations)
        ?.flat()
        ?.map((designation) => ({
          value: designation?.id,
          label: designation?.name,
        })) || [];
    return [
      { value: "", label: t("officePolicyManagement.allRoles") },
      ...designationOptions,
    ];
  }, [staffRoleData, t]);

  const filteredStaff = useMemo(() => {
    const list = staffData?.data || [];
    if (!searchText.trim()) return list;
    const normalizedSearch = searchText.trim().toLowerCase();
    return list.filter((staff) => {
      const fullName =
        `${staff?.user?.firstName || ""} ${staff?.user?.lastName || ""}`
          .trim()
          .toLowerCase();
      return fullName.includes(normalizedSearch);
    });
  }, [staffData, searchText]);

  const handleAddTimesheet = () => {
    if (!selectedStaffId) {
      toast.error(t("staffAttendance.selectAtLeastOneStaffMember"));
      return;
    }
    const selectedStaff = filteredStaff.find(
      (staff) => staff.id === selectedStaffId,
    );
    if (!selectedStaff) {
      toast.error(t("staffAttendance.selectAtLeastOneStaffMember"));
      return;
    }
    onSelectStaff(selectedStaff);
    onClose();
  };

  const addDefaultSrc = (event) => {
    event.target.src = userDefaultImage;
    event.target.onerror = null;
  };

  const loading = hideOfficeAndRoleFilters
    ? staffLoading
    : staffLoading || officeLoading || roleLoading;

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      className="modal-dialog-centered modal-lg modal-width-660"
      modalClassName="custom-modal"
    >
      {loading && <Loader />}
      <span className="close-btn" onClick={onClose}>
        <img src={crossIcon} alt="close" />
      </span>

      <ModalBody className={styles["staff-list-modal-dialog"]}>
        <Text size="25px" marginBottom="10px" weight="500" color="#111b45">
          <span className="modal-title-25">
            {t("accountOwner.selectEmployees")}
          </span>
        </Text>

        <div className={"search-box w-100 " + styles["search-box"]}>
          <input
            type="text"
            placeholder={t("accountOwner.searchEmployee")}
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
          />
          <span className="ico">
            <img
              src={require("assets/images/search-icon.svg").default}
              alt="icon"
            />
          </span>
        </div>

        {!hideOfficeAndRoleFilters && (
          <div className={"mb-3 " + localStyles.filter_row}>
            <div className={localStyles.filter_col}>
              <Select
                classNamePrefix="react-select"
                styles={selectStyles}
                options={officeOptions}
                value={selectedOfficeOption}
                isDisabled={
                  lockOfficeFilter ||
                  (initialOfficeId != null && initialOfficeId !== "")
                }
                onChange={(selected) =>
                  setSelectedOfficeOption(selected || allOfficesOption)
                }
                isSearchable={false}
              />
            </div>
            <div className={localStyles.filter_col}>
              <Select
                classNamePrefix="react-select"
                styles={selectStyles}
                options={roleOptions}
                value={selectedRoleOption}
                onChange={(selected) =>
                  setSelectedRoleOption(selected || allRolesOption)
                }
                isSearchable={false}
              />
            </div>
          </div>
        )}

        <ul
          className={"modal-employee-list " + styles["employee-list"]}
          id="employee-list"
        >
          {filteredStaff.length > 0 ? (
            filteredStaff.map((staff) => (
              <li key={staff.id} className="timesheet-report-radio-btn">
                <div className="ch-radio">
                  <label onClick={() => setSelectedStaffId(staff.id)}>
                    <input
                      type="radio"
                      name="selectAttendanceStaff"
                      checked={selectedStaffId === staff.id}
                      onChange={() => setSelectedStaffId(staff.id)}
                    />
                    <span className="d-flex">
                      <img
                        src={staff?.user?.profilePic || userDefaultImage}
                        width={40}
                        height={40}
                        onError={addDefaultSrc}
                        alt="profile"
                        style={{ borderRadius: "5px" }}
                      />
                      <div className="ml-2">
                        {`${staff?.user?.firstName || ""} ${
                          staff?.user?.lastName || ""
                        }`}
                        <Text size="12px" weight="400" color="#87928D">
                          {`${staff?.office?.name || ""}${
                            staff?.designation?.name
                              ? `, ${staff.designation.name}`
                              : ""
                          }`}
                        </Text>
                      </div>
                    </span>
                  </label>
                </div>
              </li>
            ))
          ) : (
            <li>
              <div className="ch-checkbox">
                <label>{t("noStaffMemberFound")}</label>
              </div>
            </li>
          )}
        </ul>

        <button
          className="button button-round button-shadow mr-sm-3 mb-3 w-sm-100"
          onClick={handleAddTimesheet}
          title={t("staff.select")}
        >
          {submitLabel || t("staff.select")}
        </button>
        <button
          className="button button-round button-border btn-mobile-link button-dark mb-md-3"
          onClick={onClose}
          title={t("cancel")}
        >
          {t("cancel")}
        </button>
      </ModalBody>
    </Modal>
  );
}

export default withTranslation()(AddTimesheetStaffModal);
